// 按功能分组导入第三方库和本地模块
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import parse from 'html-react-parser'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import CourseEnrollBar from '@/components/CourseEnrollBar'
import DetailPageCourseCard from '@/components/DetailPageCourseCard'
import { useCourse, useCourseRegionList } from '@/hook/useCourseList'
import SpinPending from '@/components/SpinPending'
import { Toast } from 'antd-mobile'
import { useOrderList } from '@/hook/useOrderList'
import dayjs from 'dayjs'
import useRefresh from '@/hook/useRefresh'
import { OrderStatus } from '@/types/info'
import { encodeOrderId } from '@/utils/orderIdEncoder'

// 定义路由
export const Route = createFileRoute('/course/detail/$id')({
  component: RouteComponent,
})
export enum CourseStatus {
  Hot = '火热销售中',
  Enrolled = '已报名',
  SoldOut = '已售罄',
  TimeOut = '报名已截止',
  Early = '未到报名时间',
}
/**
 * 课程详情页路由组件
 * 根据课程 ID 从模拟数据中查找课程信息并渲染课程详情页
 * @returns 课程详情页 JSX 元素
 */
function RouteComponent() {
  const navigate = useNavigate()

  // 获取路由参数，添加类型注解
  const { id }: { id?: string } = useParams({
    from: '/course/detail/$id',
  })
  // const { isLoading: courseIsLoading } = useCourseObjList()
  const { isLoading: courseIsLoading, data: course } = useCourse(Number(id))
  // 若未获取到课程 ID，返回 null
  if (!id) {
    return <SpinPending />
  }
  const { data: orderList, isFetching: fetchingOrder } = useOrderList()
  const {
    data: regionList,
    isLoading: isLoadingRegionList,
    refetch: refetchRegionList,
  } = useCourseRegionList(Number(id))
  useRefresh([refetchRegionList])
  if (fetchingOrder || isLoadingRegionList) return <SpinPending />
  // 从模拟数据中查找课程信息
  const enrollStartTime = dayjs(course?.startTime).toDate()
  const enrollEndTime = dayjs(course?.endTime).toDate()
  const now = Date.now()
  const enrollStartTimeStamp = enrollStartTime.getTime()
  const enrollEndTimeStamp = enrollEndTime.getTime()

  // 若未找到课程信息，返回 null
  if (!course || !regionList) {
    return <SpinPending />
  }
  // 检查用户是否已报名该课程
  const isEnrolled = orderList
    ?.filter((order) => order.order_status !== OrderStatus.CANCELLED)
    .some((order) => order['course_id'].toString() === id)
  let inventory = 0
  regionList.forEach((region) => {
    inventory += region.hasInventory
  })
  // 课程状态
  let courseStatus: CourseStatus
  if (now < enrollStartTimeStamp) {
    courseStatus = CourseStatus.Early
  } else if (now > enrollEndTimeStamp) {
    courseStatus = isEnrolled ? CourseStatus.Enrolled : CourseStatus.TimeOut
  } else {
    courseStatus = isEnrolled
      ? CourseStatus.Enrolled
      : inventory <= 0
        ? CourseStatus.SoldOut
        : CourseStatus.Hot
  }
  const orderId = orderList?.find(
    (order) => order['course_id'].toString() === id,
  )?.id
  // 课程描述，进行安全过滤并解析 HTML 字符串
  const courseHTML = marked.parse(course.courseDesc ?? '') as string
  const courseHTMLSanitized = DOMPurify.sanitize(courseHTML)
  const courseDescription = parse(courseHTMLSanitized)
  const courseInventory = regionList.reduce(
    (acc, cur) => acc + cur.hasInventory,
    0,
  )
  return (
    <div className="bg-[var(--bg-color)]  mx-auto min-h-[100vh]">
      {courseIsLoading ? (
        <SpinPending />
      ) : (
        <>
          <DetailPageCourseCard
            course={course}
            inventory={courseInventory}
            courseStatus={courseStatus}
          />
          <div className="mt-4 course-description">{courseDescription}</div>
        </>
      )}
      <CourseEnrollBar
        onClick={() => {
          // 已报名，跳转订单详情页
          if (courseStatus === CourseStatus.Enrolled) {
            Toast.show('您已报名该课程')
            if (orderId !== undefined) {
              navigate({
                to: '/order/$orderId',
                params: { orderId: encodeOrderId(orderId) },
              })
            }

            return
          }
          // 判断课程是否已满
          if (courseInventory <= 0) {
            Toast.show('该课程报名名额已满')
            return
          }
          // 判断是否到报名时间
          if (enrollStartTime > new Date()) {
            Toast.show('该课程未到报名时间')
            return
          }
          if (enrollEndTime < new Date()) {
            Toast.show('该课程报名已结束')
            return
          }
          navigate({
            to: '/enroll/inputting/$course_id',
            params: { course_id: id },
          })
        }}
        status={courseStatus}
      />
    </div>
  )
}
